import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface ManualGutterMeasurementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  tradeId: string;
  orgId: string;
  address?: string;
  onSuccess: () => void;
}

export function ManualGutterMeasurementDialog({
  open,
  onOpenChange,
  jobId,
  tradeId,
  orgId,
  address,
  onSuccess,
}: ManualGutterMeasurementDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    total_gutter_length: "",
    downspouts: "",
    corners: "",
    size: "5",
    guard_length: "",
    gutter_type: "K-Style",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: userData } = await supabase.auth.getUser();
      
      const insertData: any = {
        job_id: jobId,
        job_trade_id: tradeId,
        org_id: orgId,
        address: address || null,
        created_by: userData.user?.id,
        gutter_type: formData.gutter_type,
        size: formData.size,
      };

      if (formData.total_gutter_length) insertData.total_gutter_length = parseFloat(formData.total_gutter_length);
      if (formData.downspouts) insertData.downspouts = parseInt(formData.downspouts);
      if (formData.corners) insertData.corners = parseInt(formData.corners);
      if (formData.guard_length) insertData.guard_length = parseFloat(formData.guard_length);

      const { error } = await supabase.from("measurements_gutters").insert(insertData);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Gutter measurements saved successfully",
      });

      setFormData({
        total_gutter_length: "",
        downspouts: "",
        corners: "",
        size: "5",
        guard_length: "",
        gutter_type: "K-Style",
      });
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error("Error saving gutter measurements:", error);
      toast({
        title: "Error",
        description: "Failed to save gutter measurements",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Enter Gutter Measurements</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="total_gutter_length">Total Gutter Length (ft)</Label>
            <Input
              id="total_gutter_length"
              type="number"
              step="0.01"
              placeholder="e.g., 150"
              value={formData.total_gutter_length}
              onChange={(e) => setFormData({ ...formData, total_gutter_length: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="downspouts">Downspouts (count)</Label>
              <Input
                id="downspouts"
                type="number"
                placeholder="e.g., 4"
                value={formData.downspouts}
                onChange={(e) => setFormData({ ...formData, downspouts: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="corners">Corners (count)</Label>
              <Input
                id="corners"
                type="number"
                placeholder="e.g., 8"
                value={formData.corners}
                onChange={(e) => setFormData({ ...formData, corners: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="size">Gutter Size</Label>
            <Select value={formData.size} onValueChange={(value) => setFormData({ ...formData, size: value })}>
              <SelectTrigger id="size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 inch</SelectItem>
                <SelectItem value="6">6 inch</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gutter_type">Gutter Type</Label>
            <Select value={formData.gutter_type} onValueChange={(value) => setFormData({ ...formData, gutter_type: value })}>
              <SelectTrigger id="gutter_type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="K-Style">K-Style</SelectItem>
                <SelectItem value="Half-Round">Half-Round</SelectItem>
                <SelectItem value="Box">Box</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="guard_length">Gutter Guard Length (ft)</Label>
            <Input
              id="guard_length"
              type="number"
              step="0.01"
              placeholder="e.g., 150"
              value={formData.guard_length}
              onChange={(e) => setFormData({ ...formData, guard_length: e.target.value })}
            />
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Measurements
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
